import { memo } from 'react';
import { Box, TextField, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNumberInput } from '../../hooks';

export interface ScoreInputFormProps {
  homeTeam: string;
  awayTeam: string;
  homeValue: number;
  awayValue: number;
  onHomeChange: (value: number) => void;
  onAwayChange: (value: number) => void;
  onSave: (homeScore?: number, awayScore?: number) => void;
  onCancel: () => void;
  onDelete?: () => void;
  saving: boolean;
  showDelete?: boolean;
}

// Parse input value to number, defaulting to 0 for empty/invalid
export const parseInputValue = (value: string, defaultValue: number): number => {
  if (value === '' || value === '-') return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : Math.max(0, parsed);
};

export const ScoreInputForm = memo<ScoreInputFormProps>(({
  homeTeam,
  awayTeam,
  homeValue,
  awayValue,
  onHomeChange,
  onAwayChange,
  onSave,
  onCancel,
  onDelete,
  saving,
  showDelete = false,
}) => {
  const homeInput = useNumberInput(homeValue, onHomeChange, { min: 0, allowDecimals: false });
  const awayInput = useNumberInput(awayValue, onAwayChange, { min: 0, allowDecimals: false });

  // Handle Enter key - pass current input values directly to save
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !saving) {
      // Parse current input values and pass directly to onSave
      const currentHome = parseInputValue(homeInput.value, homeValue);
      const currentAway = parseInputValue(awayInput.value, awayValue);
      onSave(currentHome, currentAway);
    }
  };

  return (
    <>
      {/* Away team first to match NFL broadcast order (Away - Home) */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
        <TextField
          size="small"
          type="number"
          label={awayTeam}
          value={awayInput.value}
          onChange={awayInput.onChange}
          onBlur={awayInput.onBlur}
          onKeyDown={handleKeyDown}
          sx={{ width: 80 }}
          inputProps={{ min: 0, 'data-testid': 'score-away-input' }}
        />
        <Typography>-</Typography>
        <TextField
          size="small"
          type="number"
          label={homeTeam}
          value={homeInput.value}
          onChange={homeInput.onChange}
          onBlur={homeInput.onBlur}
          onKeyDown={handleKeyDown}
          sx={{ width: 80 }}
          inputProps={{ min: 0, 'data-testid': 'score-home-input' }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={() => onSave()} disabled={saving} data-testid="score-save-button">
          Save
        </Button>
        <Button size="small" onClick={onCancel} disabled={saving} data-testid="score-cancel-button">
          Cancel
        </Button>
        {showDelete && onDelete && (
          <Tooltip title="Delete this score">
            <IconButton size="small" color="error" onClick={onDelete} disabled={saving} sx={{ ml: 'auto' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
});

ScoreInputForm.displayName = 'ScoreInputForm';

export default ScoreInputForm;
